import React, { useState, useEffect, useCallback, useContext, useMemo } from 'react';
import {
  Button,
  Paper,
  TablePagination,
  Chip,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControlLabel,
  Switch,
  IconButton,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import { useParams, useHistory, Link } from 'react-router-dom';
import styles from './styles.module.css';
import DataTable from '../../utils/DataTable';
import Loader from '../../utils/Loading';
import Info from '../../utils/Alert/Info';
import withConsoleBase from '../../utils/ConsoleBase/withConsoleBase';
import DealerServices from '../../../services/DealerServices';
import ShopServices from '../../../services/ShopServices';
import WalletRechargePopup from '../../popups/WalletRechargePopup';
import { AlertMessagesContext } from 'react-alert-messages';
import { ROUTES, PAYMENT_MODES } from '../../../const';

// Material Icons
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import EditIcon from '@mui/icons-material/Edit';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseIcon from '@mui/icons-material/Close';
import StoreIcon from '@mui/icons-material/Store';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import PhoneIcon from '@mui/icons-material/Phone';
import BusinessIcon from '@mui/icons-material/Business';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

// Normalization Helpers
function extractArray(res) {
  if (Array.isArray(res)) return res;
  if (Array.isArray(res?.data)) return res.data;
  if (Array.isArray(res?.results)) return res.results;
  if (Array.isArray(res?.data?.data)) return res.data.data;
  if (Array.isArray(res?.data?.results)) return res.data.results;
  return [];
}

const getDealerId = (d) => d?.id ?? d?.dealer_id;
const getDealerName = (d) => d?.name || d?.dealer_name || (d ? `Dealer #${getDealerId(d)}` : '');
const getDealerBalance = (d) =>
  parseFloat(d?.credit_balance ?? d?.wallet_balance ?? d?.balance ?? d?.current_balance ?? 0);

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  try {
    return new Date(dateStr).toLocaleString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch (e) {
    return String(dateStr);
  }
};

function DealerDetailsPage(props) {
  const history = useHistory();
  const routeParams = useParams();
  const dealerId = routeParams?.dealerId || props?.match?.params?.dealerId;
  const { postAlertMessage } = useContext(AlertMessagesContext);

  const [dealer, setDealer] = useState(null);
  const [loadingDealer, setLoadingDealer] = useState(false);

  // Tabs: 0 -> Payments / Transactions, 1 -> Associated Shops
  const [currentTab, setCurrentTab] = useState(0);

  // Transactions / Payments State
  const [payments, setPayments] = useState([]);
  const [paymentsCount, setPaymentsCount] = useState(0);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [paymentsPage, setPaymentsPage] = useState(0);
  const [paymentsLimit, setPaymentsLimit] = useState(10);
  const [paymentsSearch, setPaymentsSearch] = useState('');

  // Shops State
  const [shops, setShops] = useState([]);
  const [shopsCount, setShopsCount] = useState(0);
  const [shopsLoading, setShopsLoading] = useState(false);
  const [shopsPage, setShopsPage] = useState(0);
  const [shopsLimit, setShopsLimit] = useState(10);
  const [shopsSearch, setShopsSearch] = useState('');

  // Popups State
  const [rechargePopupOpen, setRechargePopupOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editFormData, setEditFormData] = useState({
    name: '',
    customer_support_number: '',
    credit_limit: '',
    type: 'normal',
    active: true,
    is_online_renewal_enabled: true,
  });
  const [savingEdit, setSavingEdit] = useState(false);

  // 1. Fetch Dealer Profile
  const fetchDealer = useCallback(async () => {
    if (!dealerId) return;
    setLoadingDealer(true);
    try {
      const res = await DealerServices.getDealer(dealerId);
      const data = res?.data || res;
      setDealer(data);
      setEditFormData({
        name: data?.name || '',
        customer_support_number: data?.customer_support_number || data?.mobile_no || '',
        credit_limit: data?.credit_limit !== undefined ? String(data.credit_limit) : '',
        type: data?.type || 'normal',
        active: data?.active !== false,
        is_online_renewal_enabled: data?.is_online_renewal_enabled !== false,
      });
    } catch (error) {
      if (postAlertMessage) {
        postAlertMessage({
          text: error?.message || 'Failed to load dealer profile',
          type: 'failed',
        });
      }
    } finally {
      setLoadingDealer(false);
    }
  }, [dealerId, postAlertMessage]);

  // 2. Fetch Payments History for this Dealer
  const fetchPayments = useCallback(
    async ({ _page = paymentsPage, _limit = paymentsLimit, search = paymentsSearch } = {}) => {
      if (!dealerId) return;
      setPaymentsLoading(true);
      try {
        const offset = _page * _limit;
        const res = await DealerServices.getDealerPayments({
          dealer_id: dealerId,
          offset,
          limit: _limit,
          search,
        });
        const list = extractArray(res);
        const count = res?.count || list.length || 0;
        setPayments(list);
        setPaymentsCount(count);
      } catch (error) {
        if (postAlertMessage) {
          postAlertMessage({
            text: error?.message || 'Failed to load transaction history',
            type: 'failed',
          });
        }
      } finally {
        setPaymentsLoading(false);
      }
    },
    [dealerId, paymentsPage, paymentsLimit, paymentsSearch, postAlertMessage]
  );

  // 3. Fetch Associated Shops for this Dealer
  const fetchShops = useCallback(
    async ({ _page = shopsPage, _limit = shopsLimit, search = shopsSearch } = {}) => {
      if (!dealerId) return;
      setShopsLoading(true);
      try {
        const offset = _page * _limit;
        const res = await ShopServices.getShops({
          dealer_id: dealerId,
          offset,
          limit: _limit,
          search,
        });
        let list = extractArray(res);

        // Fallback: in case backend doesn't filter by dealer_id on shops list
        if (list.length > 0 && list.some((s) => s.dealer_id || s?.dealer?.id)) {
          list = list.filter((s) => {
            const sid = s.dealer_id || s?.dealer?.id;
            return !sid || String(sid) === String(dealerId);
          });
        }

        const count = res?.count || list.length || 0;
        setShops(list);
        setShopsCount(count);
      } catch (error) {
        console.error('Error fetching shops for dealer:', error);
      } finally {
        setShopsLoading(false);
      }
    },
    [dealerId, shopsPage, shopsLimit, shopsSearch]
  );

  useEffect(() => {
    fetchDealer();
    fetchPayments({ _page: 0 });
    fetchShops({ _page: 0 });
  }, [fetchDealer, fetchPayments, fetchShops]);

  // Handle Edit Dealer Submit
  const handleEditSubmit = async (e) => {
    if (e) e.preventDefault();
    setSavingEdit(true);
    try {
      const payload = {
        name: editFormData.name,
        customer_support_number: editFormData.customer_support_number,
        credit_limit: parseFloat(editFormData.credit_limit) || 0,
        type: editFormData.type,
        active: editFormData.active,
        is_online_renewal_enabled: editFormData.is_online_renewal_enabled,
      };

      const res = await DealerServices.updateDealer(dealerId, payload);
      const updated = res?.data || res || {};

      setDealer((prev) => ({
        ...prev,
        ...payload,
        ...updated,
      }));

      if (postAlertMessage) {
        postAlertMessage({
          text: 'Dealer updated successfully',
          type: 'success',
        });
      }
      setEditDialogOpen(false);
    } catch (error) {
      if (postAlertMessage) {
        postAlertMessage({
          text: error?.message || 'Failed to update dealer',
          type: 'failed',
        });
      }
    } finally {
      setSavingEdit(false);
    }
  };

  // Balance calculations
  const balance = useMemo(() => getDealerBalance(dealer), [dealer]);
  const isNegative = balance < 0;
  const dealerName = getDealerName(dealer);
  const firstChar = (dealerName && dealerName[0] ? dealerName[0] : 'D').toUpperCase();

  // Payment Table Columns
  const paymentColumns = useMemo(
    () => [
      {
        label: 'ID',
        id: 'id',
        type: 'callback',
        viewRender: (row) => <span style={{ fontWeight: 600, color: '#64748b' }}>#{row?.id}</span>,
      },
      {
        label: 'Date & Time',
        id: 'created_at',
        type: 'callback',
        viewRender: (row) => formatDate(row?.created_at || row?.date),
      },
      {
        label: 'Amount',
        id: 'amount',
        type: 'callback',
        viewRender: (row) => {
          const val = parseFloat(row?.amount || 0);
          const isNeg = val < 0;
          return (
            <span
              style={{
                fontWeight: 700,
                fontSize: 14,
                color: isNeg ? '#d32f2f' : '#16a34a',
              }}
            >
              {isNeg ? '-' : '+'}₹{Math.abs(val).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
            </span>
          );
        },
      },
      {
        label: 'Payment Mode',
        id: 'payment_mode',
        type: 'callback',
        viewRender: (row) => {
          const modeObj = PAYMENT_MODES.find((m) => m.value === row?.payment_mode);
          const labelText = modeObj ? modeObj.label : row?.payment_mode || 'Cash';
          return (
            <Chip
              label={labelText}
              size="small"
              variant="outlined"
              color="primary"
              style={{ fontWeight: 600, fontSize: 11 }}
            />
          );
        },
      },
      {
        label: 'Reference / Txn No',
        id: 'reference_no',
        type: 'callback',
        viewRender: (row) => row?.reference_no || row?.transaction_id || '-',
      },
      {
        label: 'Notes / Remarks',
        id: 'notes',
        type: 'callback',
        viewRender: (row) => row?.notes || row?.remarks || row?.description || '-',
      },
    ],
    []
  );

  // Associated Shops Table Columns
  const shopColumns = useMemo(
    () => [
      {
        label: 'ID',
        id: 'id',
        type: 'callback',
        viewRender: (row) => <span style={{ fontWeight: 600, color: '#64748b' }}>#{row?.id}</span>,
      },
      {
        label: 'Shop Name',
        id: 'name',
        type: 'callback',
        viewRender: (row) => <span style={{ fontWeight: 600, color: '#1e293b' }}>{row?.name || '-'}</span>,
      },
      {
        label: 'Mobile No',
        id: 'mobile_no',
        type: 'callback',
        viewRender: (row) => row?.mobile_no || row?.phone || '-',
      },
      {
        label: 'Status',
        id: 'status',
        type: 'callback',
        viewRender: (row) => {
          const isActive = row?.is_active !== false;
          return (
            <Chip
              label={isActive ? 'Active' : 'Inactive'}
              size="small"
              color={isActive ? 'success' : 'error'}
              variant="filled"
              style={{
                fontWeight: 600,
                fontSize: 11,
                color: '#fff',
                backgroundColor: isActive ? '#10b981' : '#ef4444',
              }}
            />
          );
        },
      },
      {
        label: 'Subscription End Date',
        id: 'subscription_end_date',
        type: 'callback',
        viewRender: (row) => formatDate(row?.subscription_end_date),
      },
      {
        label: 'Action',
        id: 'action',
        type: 'callback',
        viewRender: (row) => (
          <Button
            variant="outlined"
            size="small"
            color="primary"
            style={{ textTransform: 'none', fontWeight: 600, fontSize: 12, borderRadius: 6 }}
            onClick={(e) => {
              e.stopPropagation();
              history.push(ROUTES.EDIT_SHOP.replace(':shopId', row.id));
            }}
          >
            Edit Shop
          </Button>
        ),
      },
    ],
    [history]
  );

  return (
    <div className={styles.contentWrapper}>
      <Loader isOpen={loadingDealer} />

      {/* Back Navigation & Breadcrumb */}
      <Link to={ROUTES.DEALERS} className={styles.backNav}>
        <ArrowBackIcon fontSize="small" />
        <span>Back to Dealers List</span>
      </Link>

      {/* Main Dealer Profile Header */}
      <div className={styles.detailHeader}>
        <div className={styles.detailTitleSection}>
          <div className={styles.detailAvatar}>{firstChar}</div>
          <div>
            <h2 className={styles.detailName}>
              <span>{dealerName}</span>
              <Chip
                label={`ID #${dealerId}`}
                size="small"
                variant="outlined"
                style={{ fontWeight: 600, fontSize: 11 }}
              />
              <Chip
                label={dealer?.active !== false ? 'Active' : 'Inactive'}
                size="small"
                color={dealer?.active !== false ? 'success' : 'error'}
                style={{
                  fontWeight: 600,
                  fontSize: 11,
                  color: '#fff',
                  backgroundColor: dealer?.active !== false ? '#10b981' : '#ef4444',
                }}
              />
            </h2>
            <p className={styles.detailSubtitle}>
              Dealer Type: <b>{(dealer?.type || 'Normal').toUpperCase()}</b>
              {dealer?.flavours ? ` • Flavours: ${dealer.flavours}` : ''}
            </p>
          </div>
        </div>

        <div className={styles.headerActions}>
          <Tooltip title="Refresh Dealer Data">
            <IconButton
              onClick={() => {
                fetchDealer();
                fetchPayments();
                fetchShops();
              }}
              style={{
                backgroundColor: '#ffffff',
                border: '1px solid var(--border-color)',
                borderRadius: 8,
              }}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Button
            variant="outlined"
            color="primary"
            className={styles.actionBtn}
            startIcon={<EditIcon />}
            onClick={() => setEditDialogOpen(true)}
          >
            Edit Dealer
          </Button>

          <Button
            variant="contained"
            style={{
              backgroundColor: '#00a65a',
              color: '#ffffff',
              fontWeight: 'bold',
            }}
            className={styles.actionBtn}
            startIcon={<AccountBalanceWalletIcon />}
            onClick={() => setRechargePopupOpen(true)}
          >
            Recharge / Deduct Wallet
          </Button>
        </div>
      </div>

      {/* Top 3 Info & Summary Cards */}
      <div className={styles.detailCardsGrid}>
        {/* Card 1: Credit Balance Hero Card */}
        <div className={isNegative ? styles.heroBalanceCardNegative : styles.heroBalanceCard}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span className={styles.infoKey}>Wallet Credit Balance</span>
              <AccountBalanceWalletIcon
                style={{ color: isNegative ? '#dc2626' : '#16a34a' }}
                fontSize="small"
              />
            </div>
            <div
              style={{
                fontSize: 28,
                fontWeight: 800,
                marginTop: 8,
                color: isNegative ? '#dc2626' : '#15803d',
              }}
            >
              {isNegative ? '-' : ''}₹
              {Math.abs(balance).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
            </div>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>
              Credit Limit:{' '}
              <b>
                {parseFloat(dealer?.credit_limit || 0) > 0
                  ? `₹${parseFloat(dealer.credit_limit).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`
                  : 'Unlimited / Standard'}
              </b>
            </div>
          </div>

          <Button
            variant="contained"
            size="small"
            style={{
              marginTop: 16,
              backgroundColor: '#00a65a',
              color: '#ffffff',
              fontWeight: 700,
              textTransform: 'none',
              borderRadius: 8,
            }}
            startIcon={<AccountBalanceWalletIcon fontSize="small" />}
            onClick={() => setRechargePopupOpen(true)}
          >
            Recharge Wallet
          </Button>
        </div>

        {/* Card 2: Contact & Configuration Card */}
        <div className={styles.infoCard}>
          <div className={styles.infoCardTitle}>
            <BusinessIcon fontSize="small" />
            <span>Profile & Contact</span>
          </div>

          <div className={styles.infoRow}>
            <span className={styles.infoKey}>Customer Support Phone</span>
            <span className={styles.infoVal}>
              {dealer?.customer_support_number || dealer?.mobile_no ? (
                <a
                  href={`tel:${dealer?.customer_support_number || dealer?.mobile_no}`}
                  style={{ color: 'var(--primary-color)', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 4 }}
                >
                  <PhoneIcon fontSize="inherit" />
                  {dealer?.customer_support_number || dealer?.mobile_no}
                </a>
              ) : (
                '-'
              )}
            </span>
          </div>

          <div className={styles.infoRow}>
            <span className={styles.infoKey}>Online Renewal</span>
            <span className={styles.infoVal}>
              <Chip
                label={dealer?.is_online_renewal_enabled !== false ? 'Enabled' : 'Disabled'}
                size="small"
                variant="outlined"
                color={dealer?.is_online_renewal_enabled !== false ? 'success' : 'default'}
                style={{ fontWeight: 600, fontSize: 11 }}
              />
            </span>
          </div>

          <div className={styles.infoRow}>
            <span className={styles.infoKey}>Dealer Type</span>
            <span className={styles.infoVal}>{(dealer?.type || 'Normal').toUpperCase()}</span>
          </div>

          <div className={styles.infoRow}>
            <span className={styles.infoKey}>Flavours</span>
            <span className={styles.infoVal}>{dealer?.flavours || 'Standard'}</span>
          </div>
        </div>

        {/* Card 3: Activity & Stats Card */}
        <div className={styles.infoCard}>
          <div className={styles.infoCardTitle}>
            <ReceiptLongIcon fontSize="small" />
            <span>Activity Overview</span>
          </div>

          <div className={styles.infoRow}>
            <span className={styles.infoKey}>Total Wallet Transactions</span>
            <span className={styles.infoVal}>{paymentsCount}</span>
          </div>

          <div className={styles.infoRow}>
            <span className={styles.infoKey}>Associated Shops</span>
            <span className={styles.infoVal}>{shopsCount}</span>
          </div>

          <div className={styles.infoRow}>
            <span className={styles.infoKey}>Account Status</span>
            <span className={styles.infoVal}>
              <Chip
                label={dealer?.active !== false ? 'Active' : 'Inactive'}
                size="small"
                variant="outlined"
                color={dealer?.active !== false ? 'success' : 'error'}
                style={{ fontWeight: 600, fontSize: 11 }}
              />
            </span>
          </div>
        </div>
      </div>

      {/* Tabs Container */}
      <div className={styles.tabsContainer}>
        <div className={styles.tabHeaderBar}>
          <Tabs
            value={currentTab}
            onChange={(_, val) => setCurrentTab(val)}
            indicatorColor="primary"
            textColor="primary"
          >
            <Tab
              icon={<AccountBalanceWalletIcon fontSize="small" />}
              iconPosition="start"
              label={`Wallet Recharge History (${paymentsCount})`}
              style={{ textTransform: 'none', fontWeight: 600, minHeight: 48 }}
            />
            <Tab
              icon={<StoreIcon fontSize="small" />}
              iconPosition="start"
              label={`Associated Shops (${shopsCount})`}
              style={{ textTransform: 'none', fontWeight: 600, minHeight: 48 }}
            />
          </Tabs>
        </div>

        <div className={styles.tabBody}>
          {/* TAB 0: Payments History */}
          {currentTab === 0 && (
            <div>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: 16,
                  flexWrap: 'wrap',
                  gap: 12,
                }}
              >
                <div className={styles.searchSec} style={{ minWidth: 260 }}>
                  <input
                    type="text"
                    value={paymentsSearch}
                    onChange={(e) => setPaymentsSearch(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') fetchPayments({ _page: 0 });
                    }}
                    className={styles.searchInput}
                    placeholder="Search reference or notes..."
                  />
                  <Button
                    size="small"
                    variant="contained"
                    style={{ backgroundColor: '#00a65a', color: '#fff', textTransform: 'none' }}
                    onClick={() => fetchPayments({ _page: 0 })}
                  >
                    Search
                  </Button>
                </div>

                <Button
                  variant="contained"
                  size="small"
                  style={{
                    backgroundColor: '#00a65a',
                    color: '#ffffff',
                    fontWeight: 600,
                    textTransform: 'none',
                  }}
                  startIcon={<AccountBalanceWalletIcon />}
                  onClick={() => setRechargePopupOpen(true)}
                >
                  Add Wallet Recharge
                </Button>
              </div>

              {paymentsLoading && <Loader isOpen={true} />}

              {payments && payments.length > 0 ? (
                <Paper className={styles.tablePaper}>
                  <DataTable columns={paymentColumns} rows={payments} />
                  <TablePagination
                    rowsPerPageOptions={[10, 20, 50]}
                    component="div"
                    count={paymentsCount}
                    rowsPerPage={paymentsLimit}
                    page={paymentsPage}
                    onPageChange={(_, newPage) => {
                      setPaymentsPage(newPage);
                      fetchPayments({ _page: newPage });
                    }}
                    onRowsPerPageChange={(e) => {
                      const newLim = +e.target.value;
                      setPaymentsLimit(newLim);
                      setPaymentsPage(0);
                      fetchPayments({ _page: 0, _limit: newLim });
                    }}
                  />
                </Paper>
              ) : (
                <Info
                  title="No wallet transactions"
                  content={`No wallet recharge or adjustment transactions found for ${dealerName}.`}
                />
              )}
            </div>
          )}

          {/* TAB 1: Associated Shops */}
          {currentTab === 1 && (
            <div>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: 16,
                  flexWrap: 'wrap',
                  gap: 12,
                }}
              >
                <div className={styles.searchSec} style={{ minWidth: 260 }}>
                  <input
                    type="text"
                    value={shopsSearch}
                    onChange={(e) => setShopsSearch(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') fetchShops({ _page: 0 });
                    }}
                    className={styles.searchInput}
                    placeholder="Search shop name or mobile..."
                  />
                  <Button
                    size="small"
                    variant="contained"
                    color="primary"
                    style={{ textTransform: 'none' }}
                    onClick={() => fetchShops({ _page: 0 })}
                  >
                    Search
                  </Button>
                </div>
              </div>

              {shopsLoading && <Loader isOpen={true} />}

              {shops && shops.length > 0 ? (
                <Paper className={styles.tablePaper}>
                  <DataTable
                    columns={shopColumns}
                    rows={shops}
                    rowClickListener={(row) => history.push(ROUTES.EDIT_SHOP.replace(':shopId', row.id))}
                  />
                  <TablePagination
                    rowsPerPageOptions={[10, 20, 50]}
                    component="div"
                    count={shopsCount}
                    rowsPerPage={shopsLimit}
                    page={shopsPage}
                    onPageChange={(_, newPage) => {
                      setShopsPage(newPage);
                      fetchShops({ _page: newPage });
                    }}
                    onRowsPerPageChange={(e) => {
                      const newLim = +e.target.value;
                      setShopsLimit(newLim);
                      setShopsPage(0);
                      fetchShops({ _page: 0, _limit: newLim });
                    }}
                  />
                </Paper>
              ) : (
                <Info
                  title="No shops assigned"
                  content={`No shops are currently registered under dealer ${dealerName}.`}
                />
              )}
            </div>
          )}
        </div>
      </div>

      {/* Edit Dealer Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={savingEdit ? undefined : () => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          style: { borderRadius: 16 },
        }}
      >
        <DialogTitle
          style={{
            background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
            color: '#fff',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '16px 24px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 18, fontWeight: 700 }}>
            <EditIcon />
            <span>Edit Dealer — {dealerName}</span>
          </div>
          <IconButton
            onClick={() => setEditDialogOpen(false)}
            disabled={savingEdit}
            style={{ color: '#fff', padding: 4 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <form onSubmit={handleEditSubmit}>
          <DialogContent dividers style={{ padding: 24 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <TextField
                label="Dealer Name"
                fullWidth
                variant="outlined"
                value={editFormData.name}
                onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                required
                disabled={savingEdit}
              />

              <TextField
                label="Customer Support Number / Mobile"
                fullWidth
                variant="outlined"
                value={editFormData.customer_support_number}
                onChange={(e) =>
                  setEditFormData({ ...editFormData, customer_support_number: e.target.value })
                }
                placeholder="e.g. +91 9876543210"
                disabled={savingEdit}
              />

              <TextField
                label="Credit Limit"
                type="number"
                fullWidth
                variant="outlined"
                value={editFormData.credit_limit}
                onChange={(e) => setEditFormData({ ...editFormData, credit_limit: e.target.value })}
                placeholder="0"
                disabled={savingEdit}
                helperText="Maximum allowed credit balance before restrictions"
              />

              <TextField
                select
                label="Dealer Type"
                fullWidth
                variant="outlined"
                value={editFormData.type}
                onChange={(e) => setEditFormData({ ...editFormData, type: e.target.value })}
                disabled={savingEdit}
              >
                <MenuItem value="normal">Normal</MenuItem>
                <MenuItem value="meezan">Meezan</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
              </TextField>

              <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={editFormData.active}
                      onChange={(e) =>
                        setEditFormData({ ...editFormData, active: e.target.checked })
                      }
                      color="primary"
                    />
                  }
                  label="Active Dealer Status"
                />

                <FormControlLabel
                  control={
                    <Switch
                      checked={editFormData.is_online_renewal_enabled}
                      onChange={(e) =>
                        setEditFormData({
                          ...editFormData,
                          is_online_renewal_enabled: e.target.checked,
                        })
                      }
                      color="success"
                    />
                  }
                  label="Enable Online Renewals"
                />
              </div>
            </div>
          </DialogContent>

          <DialogActions style={{ padding: '16px 24px', justifyContent: 'flex-end', gap: 12 }}>
            <Button
              onClick={() => setEditDialogOpen(false)}
              disabled={savingEdit}
              variant="outlined"
              color="inherit"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={savingEdit}
              style={{ fontWeight: 600, padding: '8px 24px' }}
              startIcon={savingEdit ? <CircularProgress size={18} color="inherit" /> : <CheckCircleIcon />}
            >
              {savingEdit ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Wallet Recharge Popup */}
      {dealer && (
        <WalletRechargePopup
          open={rechargePopupOpen}
          onClose={() => setRechargePopupOpen(false)}
          dealers={[dealer]}
          initialDealerId={String(dealerId)}
          onSuccess={() => {
            fetchDealer();
            fetchPayments({ _page: 0 });
          }}
        />
      )}
    </div>
  );
}

export default withConsoleBase(DealerDetailsPage);
